import { useEffect, useState, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PortalNav } from "@/components/PortalNav";
import { ListChecks, Upload, Loader2, Trash2, FileText, Download } from "lucide-react";

const newStep = () => ({ label: "", required: false, _k: Math.random().toString(36).slice(2, 10) });

export default function ChecklistsAdmin() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [stepName, setStepName] = useState("");
  const [builderSteps, setBuilderSteps] = useState([newStep()]);
  const [creating, setCreating] = useState(false);

  const createStepTemplate = async () => {
    const steps = builderSteps.filter((s) => s.label.trim()).map(({ _k, ...s }) => s);
    if (!stepName.trim()) { toast.error("Template name is required"); return; }
    if (steps.length === 0) { toast.error("Add at least one step"); return; }
    setCreating(true);
    try {
      await api.post("/service/checklist-templates/steps", { name: stepName, steps });
      toast.success("Checklist template created");
      setStepName(""); setBuilderSteps([newStep()]);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed"); }
    finally { setCreating(false); }
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/service/checklist-templates"); setTemplates(data.templates || []); }
    catch (e) { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Choose a PDF file"); return; }
    const fd = new FormData();
    fd.append("name", name);
    fd.append("file", f);
    setUploading(true);
    try {
      await api.post("/service/checklist-templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template added");
      setName(""); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/service/checklist-templates/${t.id}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />
      <main className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-1"><ListChecks className="h-5 w-5 text-[#D4AF37]" /><span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Service</span></div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A] mb-6">Work Checklists</h1>

        <div className="bg-white border border-zinc-200 p-5 mb-6">
          <div className="font-display font-bold text-[#0F172A] mb-1">Add a checklist template</div>
          <p className="text-sm text-zinc-500 mb-4">Upload a fillable PDF. Its form fields are detected automatically and can be filled on any work order.</p>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="block text-xs uppercase tracking-wide text-zinc-500 mb-1.5">Template name (optional)</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="checklist-tmpl-name" placeholder="e.g. Startup Inspection" className="w-full px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
            </div>
            <input ref={fileRef} type="file" accept="application/pdf" data-testid="checklist-tmpl-file" className="text-sm" />
          </div>
          <button onClick={upload} disabled={uploading} data-testid="checklist-tmpl-upload" className="mt-4 flex items-center gap-2 bg-[#0F172A] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#1E293B] disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> Upload template</>}
          </button>
        </div>

        <div className="bg-white border border-zinc-200 p-5 mb-6">
          <div className="font-display font-bold text-[#0F172A] mb-1">Build a step checklist</div>
          <p className="text-sm text-zinc-500 mb-4">Create a reusable checklist of steps. Mark steps required; techs check them off and add notes on a work order.</p>
          <input value={stepName} onChange={(e) => setStepName(e.target.value)} data-testid="step-tmpl-name" placeholder="Checklist name (e.g. Replace Light Bulb)" className="w-full px-3 py-2.5 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] mb-3" />
          <div className="space-y-2">
            {builderSteps.map((s, i) => (
              <div key={s._k} className="flex items-center gap-2" data-testid={`step-builder-row-${i}`}>
                <input value={s.label} onChange={(e) => setBuilderSteps((xs) => xs.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} data-testid={`step-builder-label-${i}`} placeholder={`Step ${i + 1}`} className="flex-1 px-3 py-2 border border-zinc-300 text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]" />
                <label className="flex items-center gap-1.5 text-xs text-zinc-600 cursor-pointer whitespace-nowrap">
                  <input type="checkbox" checked={s.required} onChange={(e) => setBuilderSteps((xs) => xs.map((x, idx) => idx === i ? { ...x, required: e.target.checked } : x))} data-testid={`step-builder-req-${i}`} className="accent-[#7A1F2B]" /> Required
                </label>
                <button type="button" onClick={() => setBuilderSteps((xs) => xs.filter((_, idx) => idx !== i))} className="text-zinc-400 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setBuilderSteps((xs) => [...xs, newStep()])} data-testid="step-builder-add" className="mt-2 text-sm text-[#7A1F2B] hover:underline">+ Add step</button>
          <div>
            <button onClick={createStepTemplate} disabled={creating} data-testid="step-tmpl-create" className="mt-4 flex items-center gap-2 bg-[#0F172A] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#1E293B] disabled:opacity-60">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ListChecks className="h-4 w-4" /> Create checklist</>}
            </button>
          </div>
        </div>

        <div className="bg-white border border-zinc-200" data-testid="checklist-tmpl-list">
          <div className="px-5 py-3 border-b border-zinc-200 font-display font-bold text-[#0F172A]">Templates ({templates.length})</div>
          {loading ? (
            <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-zinc-400" /></div>
          ) : templates.length === 0 ? (
            <div className="p-10 text-center text-zinc-400 text-sm">No templates yet. Upload a fillable PDF above.</div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {templates.map((t) => (
                <div key={t.id} className="px-5 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#D4AF37]" /><span className="font-medium text-[#0F172A] truncate">{t.name}</span></div>
                    <div className="text-xs text-zinc-400 mt-0.5">{t.type === "steps" ? `Step checklist · v${t.version || 1} · ${t.field_count} step(s)` : `PDF · ${t.field_count} fillable field(s) · ${t.filename}`}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {t.type !== "steps" && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/service/checklist-templates/${t.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`checklist-tmpl-pdf-${t.id}`} title="View blank PDF" className="text-zinc-400 hover:text-[#D4AF37]"><Download className="h-4 w-4" /></a>}
                    <button onClick={() => remove(t)} data-testid={`checklist-tmpl-del-${t.id}`} className="text-zinc-400 hover:text-[#EF4444]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
